/**
 * The shell and the route table.
 *
 * Routes are the phase3 doc's § 3 inventory, and **all eight screens exist** as of
 * D6. A deep link a manager bookmarked, or a resumed session URL, lands on a screen
 * that reads the session and renders what its status means, rather than on a blank
 * 404 that looks like the app is broken. S4 and S6 are overlays rather than routes —
 * an item sheet and a confirm are things that happen *over* a screen, and pushing
 * history for either would make the back gesture ambiguous.
 *
 * Every session route goes through `SessionRoute`, which reads the session and
 * applies the status routing rule in one place: `Posting` opens on S7 and `Error`
 * on S5 whether a manager got there by tapping a button or by following a URL
 * from last Friday. Deep-linkable session URLs are the whole reason routing was
 * chosen over conditional rendering (§ 5: *resume-by-URL*).
 */
import { useEffect } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "./auth/AuthContext";
import { DevAuthBanner } from "./auth/DevAuthBanner";
import { SessionBar } from "./auth/SessionBar";
import { UpdatePrompt } from "./offline/UpdatePrompt";
import { Areas } from "./screens/Areas";
import { AreaSessions } from "./screens/AreaSessions";
import { Diagnostics } from "./screens/Diagnostics";
import { SessionRoute } from "./screens/SessionRoute";
import { Entry } from "./screens/Entry";
import { Review } from "./screens/Review";
import { Posting } from "./screens/Posting";
import { SessionView } from "./screens/SessionView";
import { SignIn } from "./screens/SignIn";

export function App() {
  return (
    <div className="app">
      <DevAuthBanner />
      <UpdatePrompt />
      <SessionEndedGate />
      <header className="app-header">
        <Link to="/areas" className="app-header__title">
          La Segunda Inventory Count
        </Link>
        <nav>
          <Link to="/areas">Areas</Link>
          <Link to="/about">Diagnostics</Link>
          <SessionBar />
        </nav>
      </header>

      <main className="app-main">
        <Routes>
          <Route path="/" element={<SignIn />} />
          <Route path="/areas" element={<Areas />} />
          <Route path="/areas/:areaId" element={<AreaSessions />} />
          <Route path="/about" element={<Diagnostics />} />
          <Route
            path="/areas/:areaId/sessions/:id/entry"
            element={<SessionRoute expected="entry" render={(props) => <Entry {...props} />} />}
          />
          <Route
            path="/areas/:areaId/sessions/:id/review"
            element={
              <SessionRoute expected="review" render={(props) => <Review {...props} />} />
            }
          />
          <Route
            path="/areas/:areaId/sessions/:id/posting"
            element={
              <SessionRoute expected="posting" render={(props) => <Posting {...props} />} />
            }
          />
          <Route
            path="/areas/:areaId/sessions/:id/view"
            element={<SessionRoute expected="view" render={(props) => <SessionView {...props} />} />}
          />
          {/* Unknown paths go to the area list rather than a 404 page: every real
              entry point into this app is either the root or an area URL. */}
          <Route path="*" element={<Navigate to="/areas" replace />} />
        </Routes>
      </main>

      <footer className="app-footer">
        <span className="muted small">
          Counts post to Business Central only from the review screen, and only when
          a manager confirms.
        </span>
      </footer>
    </div>
  );
}

/**
 * Renders nothing. Puts a manager whose session has ended back on S1.
 *
 * The alternative is what every screen would otherwise do on its own: show its
 * own error card saying the request failed, on a screen that can no longer load
 * anything — a dead end four taps deep, with the reason spread across four
 * different cards. One redirect, one message, one place to sign in again.
 *
 * Only in `entra` mode, and only when the identity is actually gone (`!signedIn`)
 * — a 403 leaves a manager signed in and reading a real answer about somebody
 * else's area, and moving them off that screen would hide it. Nothing here
 * touches a draft: entry lives in IndexedDB and is still there afterwards.
 */
function SessionEndedGate() {
  const { authMode, sessionMessage, signedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (authMode !== "entra") return;
    if (!sessionMessage || signedIn) return;
    if (location.pathname === "/") return;
    navigate("/", { replace: true });
  }, [authMode, sessionMessage, signedIn, location.pathname, navigate]);

  return null;
}
